/**
 * ManaCurveChart - Mana curve bar chart visualization
 *
 * Displays card count by converted mana cost (CMC) using Chart.js.
 * Compact version for left panel (200×100px).
 */
import type { ChartOptions } from 'chart.js';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

import InfoButton, { InfoList, InfoSection, InfoTip } from '@/components/InfoButton/InfoButton';

import type { ManaCurveData } from '../../lib/deckStatistics';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export interface ManaCurveChartProps {
  data: ManaCurveData;
  /** Compact mode for left panel */
  compact?: boolean;
}

/** CMC labels for x-axis */
const CMC_LABELS = ['0', '1', '2', '3', '4', '5', '6', '7+'];

function ManaCurveInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What is this?">
        <p>
          The mana curve shows how many cards you have at each <strong>mana value</strong> (CMC). A well-designed curve helps you use your
          mana efficiently every turn, minimizing wasted mana and ensuring you always have plays available.
        </p>
      </InfoSection>

      <InfoSection title="Reading the chart">
        <InfoList
          items={[
            'X-axis: Mana value (0 through 7+)',
            'Y-axis: Number of cards at that cost',
            'Lands are excluded from the curve',
            "The shape reveals your deck's speed and play pattern",
          ]}
        />
      </InfoSection>

      <InfoSection title="Common curve shapes">
        <InfoList
          items={[
            'Aggro: Heavy on 1-2 drops, drops off sharply after 3',
            'Midrange: Bell curve peaking at 2-4 mana',
            'Control: Flatter curve with more expensive spells',
            'Combo: Varies wildly based on combo pieces',
          ]}
        />
      </InfoSection>

      <InfoSection title="Format guidelines">
        <InfoList
          items={[
            '60-card: Most decks want 8-12 one-drops and 8-12 two-drops',
            'Commander: Expect higher average CMC (3.0-3.5 typical)',
            'Limited: Curve should peak around 2-3 mana',
          ]}
        />
      </InfoSection>

      <InfoTip>
        A curve that&apos;s too high means slow, clunky draws. Too low means you run out of gas. The &quot;average CMC&quot; stat below
        tells you your deck&apos;s center of gravity — aim for 2.0-2.5 in aggressive decks, 3.0-3.5 in midrange/Commander.
      </InfoTip>
    </>
  );
}

export default function ManaCurveChart({ data, compact = true }: ManaCurveChartProps): ReactElement {
  const chartData = useMemo(
    () => ({
      labels: CMC_LABELS,
      datasets: [
        {
          data: data.distribution,
          backgroundColor: '#6366f1', // aether color
          borderRadius: 2,
          barThickness: compact ? 16 : 24,
        },
      ],
    }),
    [data.distribution, compact],
  );

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1a1a1f',
          borderColor: '#2a2a32',
          borderWidth: 1,
          titleColor: '#e8e8ef',
          bodyColor: '#a8a8b8',
          padding: 8,
          displayColors: false,
          callbacks: {
            title: (items) => `CMC ${items[0].label}`,
            label: (item) => `${item.raw} card${Number(item.raw) !== 1 ? 's' : ''}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: '#6b6b7a',
            font: { size: compact ? 10 : 12 },
          },
          border: { display: false },
        },
        y: {
          display: !compact,
          grid: {
            color: '#2a2a32',
            drawTicks: false,
          },
          ticks: {
            color: '#6b6b7a',
            font: { size: 10 },
            stepSize: 1,
          },
          border: { display: false },
          beginAtZero: true,
        },
      },
    }),
    [compact],
  );

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <span className="text-ash text-xs">Cards by Mana Value</span>
        <InfoButton title="Mana Curve">
          <ManaCurveInfo />
        </InfoButton>
      </div>
      <div className={compact ? 'h-25 w-full' : 'h-50 w-full'}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}
